export const PRESENTATION = { BAG: 'bag', PAIR: 'pair', KEEPSAKE: 'keepsake' };
export const NAME_MAX_LENGTH = 16;

export const initialSelection = {
  step: 1,
  box: null,
  presentation: null,
  // Wax seals are opt-in (Figma frames 4-5). A seal turned off keeps its letter until the option changes.
  sealsOn: { bag: false, box: false },
  letters: { bag: null, box: null },
  bagNameOn: false,
  bagName: '',
  lidName: ''
};

const clearPresentation = (state) => ({
  ...state,
  presentation: null,
  sealsOn: { bag: false, box: false },
  letters: { bag: null, box: null },
  bagNameOn: false,
  bagName: '',
  lidName: ''
});

/**
 * @param {typeof initialSelection} state
 * @param {{ type: string, [key: string]: any }} action
 */
export function selectionReducer(state, action) {
  switch (action.type) {
    case 'selectBox': {
      const next = { ...state, box: action.box };
      // Keepsake is hidden with Luxe, so a chosen keepsake can't survive the switch.
      return action.box === 'luxe' && state.presentation === PRESENTATION.KEEPSAKE ? clearPresentation(next) : next;
    }
    case 'togglePresentation':
      if (state.presentation === action.presentation) return clearPresentation(state);
      return { ...clearPresentation(state), presentation: action.presentation };
    case 'clearPresentation':
      return clearPresentation(state);
    case 'setSealOn':
      return { ...state, sealsOn: { ...state.sealsOn, [action.seal]: action.on } };
    case 'setLetter':
      return { ...state, letters: { ...state.letters, [action.seal]: action.variantId } };
    case 'setBagNameOn':
      return { ...state, bagNameOn: action.on, bagName: action.on ? state.bagName : '' };
    case 'setBagName':
      return { ...state, bagName: action.text.slice(0, NAME_MAX_LENGTH) };
    case 'setLidName':
      return { ...state, lidName: action.text.slice(0, NAME_MAX_LENGTH) };
    case 'goToStep':
      return { ...state, step: action.step };
    case 'reset':
      return initialSelection;
    default:
      return state;
  }
}

/**
 * Hide/show and continue rules for steps 1-2 (brief 3.8).
 * @param {{ selection: typeof initialSelection, groups: ReturnType<import('./options').groupPackagingOptions> }} args
 */
export function getStepState({ selection, groups }) {
  const boxOptions = ['luxe', 'signature'].filter((role) => groups[role]);
  const presentationOptions = [
    groups.bag ? PRESENTATION.BAG : null,
    selection.box !== 'luxe' && groups.keepsake ? PRESENTATION.KEEPSAKE : null,
    groups.bag && groups.box ? PRESENTATION.PAIR : null
  ].filter(Boolean);
  // With one eligible box there is nothing to choose: it is picked for the customer and step 1 is not shown.
  const skipBoxStep = boxOptions.length === 1;
  const bagNameValid = !selection.bagNameOn || selection.bagName.trim().length > 0;
  // The keepsake has no name toggle: "Name on the lid" is always on, so it cannot be blank.
  const lidNameValid = selection.lidName.trim().length > 0;

  return {
    boxOptions,
    presentationOptions,
    sealEligible: { bag: Boolean(groups.stampBag), box: Boolean(groups.stampBox) },
    canContinueStep1: Boolean(selection.box),
    canContinueStep2:
      Boolean(selection.presentation) &&
      (selection.presentation !== PRESENTATION.BAG || bagNameValid) &&
      (selection.presentation !== PRESENTATION.KEEPSAKE || lidNameValid),
    hasPresentationStep: presentationOptions.length > 0,
    skipBoxStep,
    // Neither step had anything to choose, so step 3 is the first page the customer sees.
    skipBothSteps: skipBoxStep && presentationOptions.length === 0
  };
}

/**
 * The taps the system makes when the journey opens. With exactly one eligible box (e.g. no Luxe for the
 * piece's vendor or segment) it picks that box and presses CONTINUE, dispatching what the customer would.
 * @param {ReturnType<import('./options').groupPackagingOptions>} groups
 * @returns {{ actions: Array<{ type: string, [key: string]: any }>, leaveSteps: boolean }} leaveSteps: step 2
 *   has no options either, so the journey goes straight on to step 3 (without step 3 it does not open).
 */
export function autoSelectBox(groups) {
  const { boxOptions } = getStepState({ selection: initialSelection, groups });
  if (boxOptions.length !== 1) return { actions: [], leaveSteps: false };
  const pick = { type: 'selectBox', box: boxOptions[0] };
  const { hasPresentationStep } = getStepState({ selection: selectionReducer(initialSelection, pick), groups });
  return hasPresentationStep
    ? { actions: [pick, { type: 'goToStep', step: 2 }], leaveSteps: false }
    : { actions: [pick], leaveSteps: true };
}
